import { DropdownMenu, IconButton, Tooltip } from "@z/ui";
import { Columns2, Ellipsis, Maximize2, Minimize2, PanelTop, Rows2 } from "lucide-react";
import { activateTab, combineGroups, movePane, splitPane } from "../model/ops";
import { activePane, allGroups, findPane } from "../model/tree";
import type { Pane, SplitDirection, TabGroup } from "../model/types";
import { useLayout } from "./LayoutContext";
import { paneTitle } from "./PaneRegistry";

/** Visible controls for the same layout operations available through drag and keyboard. */
export function PaneMenu({ group }: { group: TabGroup }) {
  const { registry, compact, maximized, setMaximized } = useLayout();
  const pane = activePane(group);
  if (!pane) return null;
  const title = paneTitle(registry, pane);

  return (
    <div className="flex shrink-0 items-center gap-0.5 self-center pb-1.5 pl-1">
      {maximized && !compact && (
        <Tooltip content="Restore split layout">
          <IconButton label="Restore split layout" size="sm" onClick={() => setMaximized(false)}>
            <Minimize2 />
          </IconButton>
        </Tooltip>
      )}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          render={
            <IconButton label={`Pane options for ${title}`} size="sm">
              <Ellipsis />
            </IconButton>
          }
        />
        <DropdownMenu.Content align="end" aria-label="Pane options">
          <PaneArrangementItems pane={pane} />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
}

export function PaneArrangementItems({ pane }: { pane: Pane }) {
  const { tree, onChange, registry, compact, maximized, setMaximized } = useLayout();
  const groups = allGroups(tree);
  const groupId = findPane(tree, pane.id)?.group.id;
  const split = (direction: SplitDirection) => {
    setMaximized(false);
    onChange(
      splitPane(tree, pane.id, direction, {
        paneType: pane.paneType,
        params: pane.params,
        ...(pane.title !== undefined ? { title: pane.title } : {}),
      }),
    );
  };

  return (
    <>
      <DropdownMenu.Label>Arrange this pane</DropdownMenu.Label>
      <DropdownMenu.Item onClick={() => split("horizontal")}>
        <Columns2 />
        Split right
      </DropdownMenu.Item>
      <DropdownMenu.Item onClick={() => split("vertical")}>
        <Rows2 />
        Split below
      </DropdownMenu.Item>
      {groups.length > 1 && (
        <>
          <DropdownMenu.Separator />
          {!compact && (
            <DropdownMenu.Item
              onClick={() => {
                onChange(activateTab(tree, pane.id));
                setMaximized(!maximized);
              }}
            >
              {maximized ? <Minimize2 /> : <Maximize2 />}
              {maximized ? "Restore split layout" : "Maximize pane"}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Label>Move this tab to</DropdownMenu.Label>
          {groups
            .filter((g) => g.id !== groupId)
            .map((target) => {
              const active = activePane(target);
              return (
                <DropdownMenu.Item
                  key={target.id}
                  onClick={() => {
                    setMaximized(false);
                    onChange(movePane(tree, pane.id, { groupId: target.id }));
                  }}
                >
                  <PanelTop />
                  {active ? paneTitle(registry, active) : "Empty pane"}
                </DropdownMenu.Item>
              );
            })}
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            onClick={() => {
              setMaximized(false);
              onChange(combineGroups(tree));
            }}
          >
            <PanelTop />
            Combine into one pane
          </DropdownMenu.Item>
        </>
      )}
    </>
  );
}
